import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'

import { cn } from './cn'

export interface FieldProps {
  label: ReactNode
  /** A muted note under the control — units, provenance, "write-only" (I14). */
  hint?: ReactNode
  /** Server or client validation message. Sets `aria-invalid` on the control. */
  error?: ReactNode
  required?: boolean
  className?: string
  /** Receives `{id, describedBy, invalid}` so the control wires up its a11y. */
  children: (props: { id: string; describedBy?: string; invalid: boolean }) => ReactNode
}

/**
 * `.field` — label above, control, then hint or error.
 *
 * A render-prop rather than a wrapper so the label's `htmlFor`, the hint's
 * `aria-describedby` and the error's `aria-invalid` are actually connected;
 * a div that merely looks like a field is how forms become unusable with a
 * screen reader.
 */
export function Field({
  label,
  hint,
  error,
  required = false,
  className,
  children,
}: FieldProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined

  return (
    <div className={cn('field', className)}>
      <label htmlFor={id}>
        {label}
        {required ? (
          <span aria-hidden="true" className="ml-0.5 text-block-fg">
            *
          </span>
        ) : null}
      </label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {error ? (
        <div id={errorId} className="mt-1 text-micro text-block-fg">
          {error}
        </div>
      ) : hint ? (
        <div id={hintId} className="mt-1 text-micro text-muted">
          {hint}
        </div>
      ) : null}
    </div>
  )
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean
  /** Model ids, API keys and application numbers all read better monospaced. */
  mono?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid = false, mono = false, className, ...rest },
  ref,
) {
  return (
    <input
      {...rest}
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn('input', invalid && 'input-invalid', mono && 'font-mono text-meta', className)}
    />
  )
})

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid = false, className, children, ...rest },
  ref,
) {
  return (
    <select
      {...rest}
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn('input', invalid && 'input-invalid', className)}
    >
      {children}
    </select>
  )
})

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid = false, className, ...rest },
  ref,
) {
  return (
    <textarea
      {...rest}
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn('input', invalid && 'input-invalid', className)}
    />
  )
})
